// Kalender-Export-Helfer. Event-Daten kommen lokal in Europe/Berlin
// (yyyy-mm-dd + HH:MM). Wir nehmen 3 h Default-Dauer an — die meisten Events
// haben kein explizites Ende, das wäre eine kleine Doku-Zeile im Admin wert.

const DEFAULT_DURATION_HOURS = 4;

type EventLike = {
  name: string;
  eventDate: string;             // yyyy-mm-dd
  eventTime: string;             // HH:MM
  location?: string | null;
  subheading?: string | null;
};

function parts(d: string, t: string): [number, number, number, number, number] {
  const [y, mo, da] = d.split('-').map(Number);
  const [h, mi] = t.split(':').map(Number);
  return [y!, mo!, da!, h!, mi!];
}

// Berliner Wallclock + duration → Berliner End-Wallclock. Pure date-math,
// keine TZ-Konvertierung; falls Sommerzeit ändern würde, ist die Anzeige
// trotzdem konsistent weil wir Wallclock-only deklarieren.
function addHours(d: string, t: string, hours: number): { date: string; time: string } {
  const [y, mo, da, h, mi] = parts(d, t);
  const date = new Date(Date.UTC(y, mo - 1, da, h + hours, mi));
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const hh = String(date.getUTCHours()).padStart(2, '0');
  const mim = String(date.getUTCMinutes()).padStart(2, '0');
  return { date: `${yy}-${mm}-${dd}`, time: `${hh}:${mim}` };
}

// ICS / Google-Style timestamp ohne TZ-Suffix — wir kennzeichnen die Zone
// separat (TZID bei ICS, ctz bei Google).
function compactDateTime(d: string, t: string): string {
  return `${d.replaceAll('-', '')}T${t.replace(':', '')}00`;
}

function getEnd(ev: EventLike): { date: string; time: string } {
  return addHours(ev.eventDate, ev.eventTime, DEFAULT_DURATION_HOURS);
}

// Google Calendar Render-URL — neuer Tab öffnet die "neuer Eintrag"-Maske
// vorbefüllt. dates= ohne TZ + ctz= als TZ-Spec.
export function googleCalendarUrl(ev: EventLike): string {
  const end = getEnd(ev);
  const start = compactDateTime(ev.eventDate, ev.eventTime);
  const stop = compactDateTime(end.date, end.time);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: ev.name,
    dates: `${start}/${stop}`,
    ctz: 'Europe/Berlin',
    ...(ev.subheading ? { details: ev.subheading } : {}),
    ...(ev.location ? { location: ev.location } : {}),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// Outlook Web — vergleichbarer Compose-Deeplink. startdt/enddt müssen ISO
// inkl. TZ sein; wir nutzen lokale Berliner Zeit ohne TZ-Suffix, was Outlook
// als lokale Zeit interpretiert.
export function outlookCalendarUrl(ev: EventLike): string {
  const end = getEnd(ev);
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: ev.name,
    startdt: `${ev.eventDate}T${ev.eventTime}:00`,
    enddt: `${end.date}T${end.time}:00`,
    ...(ev.subheading ? { body: ev.subheading } : {}),
    ...(ev.location ? { location: ev.location } : {}),
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

// ICS-Inhalt für Download → triggert Calendar.app auf macOS/iOS, kompatibel
// mit Outlook-Desktop, Thunderbird, allen Standard-Clients.
function icsEscape(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

export function icsBlob(ev: EventLike): Blob {
  const end = getEnd(ev);
  const dtStart = compactDateTime(ev.eventDate, ev.eventTime);
  const dtEnd = compactDateTime(end.date, end.time);
  const now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  // UID muss eindeutig pro Event sein — slug+date reicht; falls Slug fehlt
  // (z.B. Public-Path), nehmen wir Name+Date als Fallback.
  const uid = `${ev.eventDate.replace(/-/g, '')}-${ev.name.replace(/\s+/g, '-')}@events.tro.de`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//TRO Events//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART;TZID=Europe/Berlin:${dtStart}`,
    `DTEND;TZID=Europe/Berlin:${dtEnd}`,
    `SUMMARY:${icsEscape(ev.name)}`,
    ...(ev.location ? [`LOCATION:${icsEscape(ev.location)}`] : []),
    ...(ev.subheading ? [`DESCRIPTION:${icsEscape(ev.subheading)}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return new Blob([lines.join('\r\n') + '\r\n'], { type: 'text/calendar;charset=utf-8' });
}

export function downloadIcs(ev: EventLike): void {
  const blob = icsBlob(ev);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${ev.name.replace(/[^a-z0-9-_]+/gi, '_')}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // kurzer Delay damit Browser den Download initiieren konnte.
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export function googleMapsUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
